import i18n from "../../i18n";
import { create } from "zustand";
import { yearFilterConfig } from "./data";
import { IFeature } from "../../interfaces/featureInterface";
import { runtimeConfig } from "../../runtimeConfig";

type AppLang = "de" | "fr" | "it" | "en";

export interface YearFilter {
    id: number;
    label: string;
    selected: boolean;
    url: string;
    infra3dCampaignId: number;
    infra3dCampaignName: string;
    infra3dLayerName: string;
    mapTopics: string[];
}

interface AppStore {
    appLangs: AppLang[];
    activeLanguage: AppLang | null;
    setLang: (lang: AppLang) => void;
    arcGisPortalUrl: string;
    setArcGisPortalUrl: (portalUrl: string) => void;
    lineFilterValues: Array<{ id: number; label: string; selected: boolean }>;
    toggleLineFilter: (lineFilter: number) => void;
    fetchLineFilters: () => void;
    yearFilterValues: YearFilter[];
    setYearFilter: (id: number) => void;
    activeChartFilterValues: Array<{
        id: number | null;
        fieldName: string | null;
        type: string | null;
    }>;
    setActiveChartFilter: (id: number | null, fieldName: string | null, type: string | null) => void;
    displayHeight: string;
    setDisplayHeight: (height: string) => void;
    infra3dIsOpen: boolean;
    setInfra3dIsOpen: (setOpen: boolean) => void;
    modalPopupIsOpen: boolean;
    setModalPopupIsOpen: (setOpen: boolean) => void;
    currentPopup: string;
    setCurrentPopup: (currentPopup: string) => void;
    infra3dUser: string;
    infra3dLogin: string;
    setInfra3dLogin: (user: string, login: string) => void;
    group: string;
    setGroup: (group: string) => void;
    environment: string | null;
    setEnvironment: (environment: string) => void;
    featureFlags: IFeature[];
    setFeatureFlags: (featureFlags: IFeature[]) => void;
    errorReportUrl: string;
    setErrorReportUrl: (url: string) => void;
}

interface ArcGisUniqueValueFeature {
    attributes: Record<string, string | number | null>;
}

interface ArcGisUniqueValueResponse {
    features?: ArcGisUniqueValueFeature[];
}

interface ArcGisErrorResponse {
    error?: {
        code: number;
        message: string;
        messageCode?: string;
        details?: string[];
    };
}

interface UniqueValueInfo {
    value: string;
}

const uniqueValuesCache = new Map<string, UniqueValueInfo[]>();

const getArcGisOAuthToken = async (layerUrl: string): Promise<string> => {
    const portalUrl = useAppStore.getState().arcGisPortalUrl;
    const sharingUrl = portalUrl ? `${portalUrl.replace(/\/$/, "")}/sharing` : "";

    const [{ default: IdentityManager }] =
        await Promise.all([
            import("@arcgis/core/identity/IdentityManager"),
        ]);

    const credential =
        (sharingUrl ? IdentityManager.findCredential(sharingUrl) : undefined) ??
        await IdentityManager.getCredential(layerUrl);

    return credential.token;
};


const getUniqueValues = async (
    field: string,
    layerUrl: string
): Promise<UniqueValueInfo[]> => {

    const cacheKey = `${layerUrl.replace(/\/$/, "")}:${field}`;
    const cachedValues = uniqueValuesCache.get(cacheKey);

    if (cachedValues) {
        return cachedValues;
    }

    const params = new URLSearchParams({
        f: "json",
        where: "1=1",
        returnGeometry: "false",
        returnDistinctValues: "true",
        outFields: field,
        orderByFields: field,
    });

    if (useAppStore.getState().environment !== "Showcase") {
        const token = await getArcGisOAuthToken(layerUrl);
        params.set("token", token);
    }

    const normalizedLayerUrl = layerUrl.replace(/\/$/, "");

    const queryUrl = /\/FeatureServer\/\d+$/i.test(normalizedLayerUrl)
        ? `${normalizedLayerUrl}/query`
        : `${normalizedLayerUrl}/0/query`;

    const url = `${queryUrl}?${params.toString()}`;
    const response = await fetch(url);

    if (!response.ok) {
        throw new Error(`Failed to fetch unique values for ${field}`);
    }

    const data = (await response.json()) as ArcGisUniqueValueResponse & ArcGisErrorResponse;

    if (data.error) {
        throw new Error(
            `ArcGIS query failed: ${data.error.code} ${data.error.message}`
        );
    }
    const result = (data.features ?? [])
        .map((feature) => feature.attributes[field])
        .filter((value): value is string | number => value !== null && value !== undefined)
        .map((value) => ({ value: String(value) }));

    uniqueValuesCache.set(cacheKey, result);

    return result;
};

const useAppStore = create<AppStore>((set, get) => ({
    appLangs: ["de", "fr", "it", "en"], // All supported languages
    activeLanguage: null,
    setLang: (lang: AppLang) => {
        set(() => ({ activeLanguage: lang }));
        i18n.changeLanguage(lang);
        localStorage.setItem("lang", lang);

        import("@arcgis/core/intl").then((intl) => {
            intl.setLocale(lang);
        });
    },
    arcGisPortalUrl: "",
    setArcGisPortalUrl: (portalUrl: string) => {
        set(() => ({ arcGisPortalUrl: portalUrl }));
    },
    lineFilterValues: [],
    toggleLineFilter: (lineFilter) => {
        const result = get().lineFilterValues.map((l) => {
            if (l.id === lineFilter) l.selected = !l.selected;
            return l;
        });
        set(() => ({ lineFilterValues: result }));
    },
    fetchLineFilters: () => {
        const selectedYear = get().yearFilterValues.find((yf) => yf.selected);
        const layerUrl = selectedYear?.url;

        if (!layerUrl) {
            set(() => ({ lineFilterValues: []}));
            return;
        }
        getUniqueValues("Linie", layerUrl).then((res) => {
            const lineFilterValues = res.map((info, index) => {
                // Check if the Filter is selected currently and reapply to new filters
                const currentlySelected = get().lineFilterValues.find(
                    (lfValue) => lfValue.label === info.value
                )?.selected;
                return { id: index, label: info.value, selected: currentlySelected };
            }) as Array<{ id: number; label: string; selected: boolean }>;
            lineFilterValues.sort((a, b) => a.label.localeCompare(b.label));
            set(() => ({ lineFilterValues: lineFilterValues }));
        });
    },
    yearFilterValues: [],
    setYearFilter: (id) => {
        const result = get().yearFilterValues.map((yearFilter) => {
            yearFilter.selected = yearFilter.id === id;
            return yearFilter;
        });
        set(() => ({ yearFilterValues: result }));
        get().fetchLineFilters();
    },
    activeChartFilterValues: [{ id: null, fieldName: null, type: null }],
    setActiveChartFilter: (id, fieldName, type) => {
        const result = get().activeChartFilterValues.map((filter) => {
            filter.id = id;
            filter.fieldName = fieldName;
            filter.type = type;
            return filter;
        });
        set(() => ({ activeChartFilterValues: result }));
    },
    displayHeight: "middle",
    setDisplayHeight: (height) => {
        set(() => ({ displayHeight: height }));
    },
    infra3dIsOpen: true,
    setInfra3dIsOpen: (setOpen) => {
        set(() => ({ infra3dIsOpen: setOpen }));
    },
    infra3dUser: "",
    infra3dLogin: "",
    setInfra3dLogin: (user: string, login: string) => {
        set(() => ({ infra3dUser: user, infra3dLogin: login }));
    },
    group: "",
    setGroup: (group: string) => {
        set(() => ({ group: group }));
    },
    modalPopupIsOpen: false,
    setModalPopupIsOpen: (setOpen) => {
        set(() => ({ modalPopupIsOpen: setOpen }));
    },
    currentPopup: "",
    setCurrentPopup: (popup) => {
        set(() => ({ currentPopup: popup }));
    },
    environment: localStorage.getItem("environment"),
    setEnvironment: (environment) => {
        set(() => ({ environment: environment }))
        if (runtimeConfig.ENVIRONMENT === "Development") {
            localStorage.setItem("environment", environment);
            window.location.reload();
        }
    },
    featureFlags: [],
    setFeatureFlags: (featureFlags) => {
        set(() => ({ featureFlags: featureFlags }));
    },
    errorReportUrl: "/faultReport",
    setErrorReportUrl: (url: string) => {
        set(() => ({ errorReportUrl: url }))
    }
}));

// set language
let lang = i18n.language.slice(0, 2) as AppLang;
if (
    !useAppStore.getState().appLangs.includes(lang) ||
    localStorage.getItem("lang") == null
) {
    lang = "de";
} else {
    lang = localStorage.getItem("lang") as AppLang;
}
useAppStore.getState().setLang(lang);
switch (runtimeConfig.ENVIRONMENT) {
    case "Development":
        if (localStorage.getItem("environment") == null) {
            useAppStore.getState().setEnvironment(runtimeConfig.ENVIRONMENT);
        }
        break;
    case "Showcase":
    case "Production":
        useAppStore.getState().setEnvironment(runtimeConfig.ENVIRONMENT);
        if (localStorage.getItem("environment") != null) {
            localStorage.removeItem("environment");
        }
        break;
    default:
        break;
}
// fetch filters on init
useAppStore.setState(() => ({ yearFilterValues: yearFilterConfig }));
// useAppStore.getState().fetchLineFilters();

export default useAppStore;
